import styled from '@emotion/styled';
import React from 'react';
import { TestId } from '@components/test-ids';
import { ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { mq } from '@styles/breakpoints';
import { Icon } from '@components/common/generic-icon';
import { ZIndex } from '@styles/z-index';
import { boxShadowMixins } from '@styles/mixins';
import { Amount, Price } from '@model/common';
import { LazyLoadBackgroundImage } from '@components/common/lazy-load';
import { RippleCard } from '@components/common/ripple-card';
import { LinkItem } from '@model/iceberg';
import Link from 'next/link';

export interface ExperienceProductTileProps {
  name?: string;
  price?: Price;
  imageUrl?: string;
  days?: number;
  link: LinkItem;
}

const Container: any = styled(RippleCard)(({ theme }: ThemeProps) => ({
  width: '100%',
  height: 400,
  backgroundColor: theme.custom.colors.group10.lighter,
  borderRadius: 10,
  overflow: 'hidden',
  position: 'relative',

  [mq.medium]: {
    height: 500
  }
}));

const TileWrapper: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  width: '100%',
  overflow: 'hidden',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  position: 'relative',
  height: '100%',
  cursor: 'pointer',
  padding: theme.custom.spacing.large,
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).light,

  ['&:hover']: {
    backgroundSize: '110%'
  }
}));

const BackgroundImage: any = styled(LazyLoadBackgroundImage)(({ theme }: ThemeProps) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  transition: `transform ${theme.custom.transitions.longSmooth}`,
  zIndex: ZIndex.BASE,

  ['&:hover']: {
    transform: 'scale(1.2)'
  }
}));

const TourInfoCard: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.custom.colors.overlay.dark,
  backdropFilter: 'blur(10px)',
  minHeight: 134,
  maxWidth: 370,
  padding: theme.custom.spacing.large,
  borderRadius: 6,
  color: theme.custom.colors.white,
  zIndex: ZIndex.CONTENT,
  pointerEvents: 'none'
}));

const TourName: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  margin: 0,
  lineHeight: '2.5rem'
}));

const TourLength: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  margin: 0,

  ['i']: {
    fontSize: '1.4rem',
    marginRight: theme.custom.spacing.xSmall
  }
}));

const TourPrice: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  margin: 0
}));

const TourPriceAmount = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  margin: 0
}));

const StyledIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.white
}));

export const ExperienceProductTile = ({ name, price, imageUrl, days, link }: ExperienceProductTileProps) => {
  const t: any = useI18NextContext();
  return (
    <Container data-testid={TestId.experienceProductTile.container} fixedHeight>
      <Link style={{ textDecoration: 'none' }} {...link}>
        <TileWrapper>
          <BackgroundImage src={imageUrl} />
          <TourInfoCard data-testid={TestId.experienceProductTile.infoCard}>
            <TourName data-testid={TestId.experienceProductTile.tourName}>{name}</TourName>
            <TourLength data-testid={TestId.experienceProductTile.tourLength}>
              <StyledIcon name={'mhi-map-pin-outline'} />
              <span>{t('experiences__tile--subtitle', { days })}</span>
            </TourLength>
            {!!price?.amount && (
              <TourPrice data-testid={TestId.experienceProductTile.tourPrice}>
                {t('common__label--from')}{' '}
                <TourPriceAmount>{new Amount(price?.amount, price?.currency.code).format()}</TourPriceAmount>{' '}
                {t('common__label--per-person-short')}
              </TourPrice>
            )}
          </TourInfoCard>
        </TileWrapper>
      </Link>
    </Container>
  );
};
